import { motion } from "framer-motion";
import { biography, aboutPortrait, closingQuote } from "../../data/portfolio";

export const About = () => {
  const [opener, ...rest] = biography;
  // The last biography line is now presented as a decorative pull quote outside the body,
  // so exclude it from the supporting paragraphs.
  const middle = rest.slice(0, -1);

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-40 sm:py-56 lg:py-64 px-6 sm:px-12"
    >
      <div className="max-w-[1400px] mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="text-[10px] uppercase tracking-[0.4em] text-white/40 mb-20 sm:mb-28 text-center"
        >
          <span style={{ color: "var(--accent-amber)" }}>01</span> — Biography
        </motion.p>

        <div className="grid grid-cols-12 gap-8 sm:gap-12 lg:gap-20 items-start">
          {/* Portrait */}
          <motion.figure
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.2, ease: [0.19, 1, 0.22, 1] }}
            className="col-span-12 md:col-span-5 lg:col-span-5 md:sticky md:top-24"
          >
            <div className="relative w-full overflow-hidden bg-neutral-900 group">
              <img
                src={aboutPortrait.src}
                alt={aboutPortrait.alt}
                data-testid="about-portrait"
                loading="lazy"
                decoding="async"
                className="block w-full h-auto transition-transform duration-[2000ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.02]"
                style={{ filter: "contrast(1.05) brightness(1.02) saturate(0.95)" }}
              />
              <div
                className="absolute inset-0 pointer-events-none mix-blend-soft-light opacity-30"
                style={{
                  background:
                    "linear-gradient(160deg, rgba(200,162,91,0.18) 0%, transparent 55%, rgba(28,42,58,0.20) 100%)",
                }}
              />
            </div>
            <figcaption className="mt-4 flex items-baseline justify-between text-[10px] uppercase tracking-[0.3em] text-white/35">
              <span>Portrait</span>
              <span style={{ color: "var(--accent-amber)" }}>V.R.</span>
            </figcaption>
          </motion.figure>

          {/* Biography */}
          <div className="col-span-12 md:col-span-7 lg:col-span-7 md:pt-4">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.1, ease: [0.19, 1, 0.22, 1] }}
              className="font-serif text-3xl sm:text-4xl lg:text-[42px] leading-[1.15] tracking-tight text-white font-light"
            >
              {opener}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              whileInView={{ opacity: 1, scaleX: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, delay: 0.3, ease: [0.19, 1, 0.22, 1] }}
              className="h-px w-16 my-10 sm:my-12 origin-left"
              style={{ backgroundColor: "var(--accent-amber)" }}
            />

            <div className="space-y-6 sm:space-y-7 max-w-[560px]">
              {middle.map((line, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.08 }}
                  className="text-[15px] sm:text-base leading-[1.75] text-white/65 font-sans"
                >
                  {line}
                </motion.p>
              ))}
            </div>

            {/* Closing personal quote — editorial pull quote with signature */}
            {closingQuote && (
              <motion.figure
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1.2, delay: 0.4, ease: [0.19, 1, 0.22, 1] }}
                className="relative mt-24 sm:mt-32 lg:mt-36 max-w-[620px]"
                data-testid="about-closing-quote"
              >
                <span
                  aria-hidden
                  className="absolute -top-8 -left-2 sm:-top-10 sm:-left-4 font-serif italic leading-none pointer-events-none select-none"
                  style={{
                    fontSize: "120px",
                    color: "var(--accent-amber)",
                    opacity: 0.35,
                  }}
                >
                  &ldquo;
                </span>

                <blockquote className="relative">
                  <p className="font-serif italic text-[26px] sm:text-[30px] lg:text-[34px] leading-[1.28] tracking-[-0.005em] text-white font-light">
                    {closingQuote.text}
                  </p>
                </blockquote>

                <figcaption className="mt-10 sm:mt-12 flex items-center gap-4">
                  <span
                    className="h-px w-10"
                    style={{ backgroundColor: "var(--accent-amber)" }}
                  />
                  <span className="font-serif text-[13px] sm:text-[14px] tracking-[0.02em] text-white/70">
                    {closingQuote.attribution}
                  </span>
                </figcaption>
              </motion.figure>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
